import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Quote as QuoteIcon, X, Send, Download, PhoneCall } from "lucide-react";
import { Toaster, toast } from "sonner";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useData";

const inputCls = "w-full rounded-xl border border-navy-100 bg-white px-4 py-3 text-sm text-navy-800 placeholder:text-navy-400 focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-colors";

export function QuoteFloating() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { data: services } = useFetch("/services");
  const [f, setF] = useState({ name: "", email: "", organization: "", service_interest: "", message: "" });

  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; }, [open]);
  const u = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact-messages", {
        name: f.name, email: f.email, organization: f.organization,
        service_interest: f.service_interest,
        subject: `Quote request: ${f.service_interest || "general"}`,
        message: f.message,
        source: "quote_request",
      });
      toast.dismiss();
      toast.success("Quote request received. Our team will reach out shortly.", { id: "quote-success" });
      setF({ name: "", email: "", organization: "", service_interest: "", message: "" });
      setOpen(false);
    } catch (err) {
      toast.dismiss();
      toast.error("Could not send your request. Please try again.", { id: "quote-error" });
    } finally { setBusy(false); }
  };

  return (
    <>
      <Toaster richColors position="top-center" />
      <button
        onClick={() => setOpen(true)}
        data-testid="quote-fab"
        className="hidden lg:inline-flex fixed bottom-6 right-6 z-40 items-center gap-2 px-5 py-3 rounded-full bg-navy text-white text-sm font-semibold shadow-card hover:bg-teal transition-colors"
      >
        <QuoteIcon className="h-4 w-4" /> Request a quote
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex" data-testid="quote-panel">
          <div className="absolute inset-0 bg-navy/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative ml-auto h-full w-full max-w-md bg-white overflow-y-auto">
            <div className="p-6 flex items-center justify-between border-b border-navy-50">
              <h2 className="font-display font-semibold text-xl text-navy-800">Request a quote</h2>
              <button onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-navy-50" data-testid="quote-close"><X className="h-5 w-5 text-navy" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <input data-testid="quote-name" required value={f.name} onChange={u("name")} placeholder="Your name *" className={inputCls} />
              <input data-testid="quote-email" type="email" required value={f.email} onChange={u("email")} placeholder="Email *" className={inputCls} />
              <input data-testid="quote-org" value={f.organization} onChange={u("organization")} placeholder="Organisation" className={inputCls} />
              <select data-testid="quote-service" value={f.service_interest} onChange={u("service_interest")} className={inputCls}>
                <option value="">Select a service of interest</option>
                {(services || []).map((s) => <option key={s.slug} value={s.slug}>{s.title}</option>)}
              </select>
              <textarea data-testid="quote-message" required value={f.message} onChange={u("message")} placeholder="Tell us about your needs..." rows={5} className={`${inputCls} resize-y`} minLength={10} />
              <button type="submit" disabled={busy} data-testid="quote-submit" className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal transition-colors disabled:opacity-60">
                <Send className="h-4 w-4" /> {busy ? "Sending..." : "Send request"}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export function StickyMobileCta() {
  const [show, setShow] = useState(true);
  const [lastY, setLastY] = useState(0);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y < 80) setShow(true);
      else if (y > lastY + 6) setShow(false);
      else if (y < lastY - 6) setShow(true);
      setLastY(y);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [lastY]);

  if (pathname === "/contact") return null;
  return (
    <div
      data-testid="mobile-cta-bar"
      className={`lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-navy-100 shadow-card transition-transform duration-300 ${show ? "translate-y-0" : "translate-y-full"}`}
    >
      <div className="grid grid-cols-1 gap-2 p-3">
        <Link to="/contact" className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-navy text-white text-xs font-semibold">
          <PhoneCall className="h-3.5 w-3.5" /> Contact us
        </Link>
      </div>
    </div>
  );
}
